import React, { useState, useEffect, useRef } from "react";
import CustomDataGrid from "../components/CustomDataGrid";
import EntityDetails from "../components/EntityDetails";
import { Button, Alert, Spinner } from "react-bootstrap";
import JSZip from "jszip";
import QRCode from "qrcode";
import { saveAs } from "file-saver";
import {
  FaTrashAlt,
  FaCheckCircle,
  FaCloudUploadAlt,
  FaDownload,
} from "react-icons/fa";
import {
  campaignGridColumns,
  campaignMapping,
  campaignRowMapping,
  generateFormData,
  generateGridColumns,
  generateGridRows,
  validateCsvContent
} from "../utils/PageUtils";
import { fetchCampaignData, uploadContactsToAPI } from "../utils/api";
import "../CustomStyle.css";
import PageHelmet from "../components/PageHelmet";
import { useSearchParams } from 'react-router-dom'

const UploadContacts = ({ userRole, bearerToken }) => {
  const fileInputRef = useRef(null);

  const [campaignsData, setCampaignsData] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [zipFile, setZipFile] = useState(null);
  const [isUploaded, setIsUploaded] = useState(false);
  const [refreshGrid, setRefreshGrid] = useState(true);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [searchParams] = useSearchParams()
  const testMode = searchParams.get('test-mode')
  const [isUploading, setIsUploading] = useState(false);

  useEffect(() => {
    const loadCampaigns = async () => {
      if (!bearerToken) {
        return;
      }
      if (campaignsData.length === 0) {
        setIsLoading(true);
      }
      try {
        const data = await fetchCampaignData(bearerToken);
        if (data?.records) {
          const sortedCampaigns = data.records.sort((a, b) => new Date(b.created) - new Date(a.created));
          setCampaignsData(sortedCampaigns);
        }
      } catch (error) {
        setError("An error occurred while fetching campaigns.");
      } finally {
        setIsLoading(false);
        if (refreshGrid) {
          setRefreshGrid(false);
        }
      }
    };
    if (refreshGrid) {
      loadCampaigns();
    }
  }, [refreshGrid]);

  useEffect(() => {
    if (!selectedCampaign || campaignsData.length === 0) return

    const updatedCampaign = campaignsData.find(
      (c) => c.campaign_name === selectedCampaign.campaign_name
    )

    if (updatedCampaign) {
      setSelectedCampaign(updatedCampaign)
    }
  }, [campaignsData]);

  const handleRowClick = (e) => {
    setSelectedCampaign(null);
    resetUploadState();

    const selected = campaignsData[e.id - 1];
    setTimeout(() => {
      setSelectedCampaign(selected);
    }, 0);
    setSelectedRowId(e.id);
  };

  const handleFileChange = async (event) => {
    const uploadedFile = event.target.files[0];
    resetMessages();
    setFile(null);

    if (!uploadedFile) return;

    const rejectFile = (message) => {
      setUploadError(message);
      if (fileInputRef.current) fileInputRef.current.value = null;
    };

    const validationError = await validateCsvContent(uploadedFile);
    if (validationError) {
      return rejectFile(validationError);
    }

    setFile(uploadedFile);
  };

  const resetMessages = () => {
    setUploadError("");
    setInfo(null);
    if (!isUploaded) {
      setZipFile(null);
    }
  };

  const handleRemoveFile = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const resetUploadState = () => {
    setFile(null);
    setZipFile(null);
    setInfo(null);
    setError(null);
    setIsUploaded(false);

    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  const handleUpload = async () => {
    if (!file || !selectedCampaign) return;
    setIsUploaded(true);
    setIsUploading(true);

    try {
      const payload = {
        campaign: selectedCampaign.campaign_name,
        destination_url: selectedCampaign.destination_url,
      };
      if (testMode) {
        payload.mode = "test";
      }

      const result = await uploadContactsToAPI(file, payload, bearerToken);
      if (result.success) {
        setInfo(result.message);
        if (testMode) {
          const data = result.data;
          await generateQRCodesAndZip(data);
        }
        setFile(null);
        setIsUploaded(false);
        if (fileInputRef.current) {
          fileInputRef.current.value = null;
        }
        setRefreshGrid(true);
      } else {
        setError(result.message);
      }
    } catch {
      setError("An error occurred while uploading contacts.");
    } finally {
      setIsUploading(false);
    }
  };

  const generateQRCodesAndZip = async (data) => {
    const zip = new JSZip();
    const qrZip = new JSZip();

    for (const row of data) {
      const url = row["o.rp.trackableurl"];
      const name = row["o.rp.qrcode_img"];

      if (url !== "Error" && name) {
        try {
          const qrDataUrl = await QRCode.toDataURL(url, {
            scale: 10,
            margin: 1,
          });
          const qrBlob = await (await fetch(qrDataUrl)).blob();
          qrZip.file(`${name}.png`, qrBlob);
        } catch (err) {
          setError("An error occurred while generating QR codes.");
        }
      }
    }
    const csvString = generateCsvString(data);
    zip.file(
      "processed_contacts.csv",
      new Blob([csvString], { type: "text/csv" })
    );
    zip.file("qrcodes.zip", await qrZip.generateAsync({ type: "blob" }));

    const finalZip = await zip.generateAsync({ type: "blob" });
    const timestamp = new Date().toISOString().replace(/[-:.]/g, "_");
    const name = selectedCampaign?.campaign_name || "unknown";

    setZipFile({
      blob: finalZip,
      name: `campaign_${name}_upload_${timestamp}.zip`,
    });
  };

  const generateCsvString = (data) => {
    const headers = Object.keys(data[0]).join(",");
    const body = data.map((row) => Object.values(row).join(",")).join("\n");
    return `${headers}\n${body}`;
  };

  const campaignFormData = selectedCampaign
    ? generateFormData(selectedCampaign, campaignMapping)
    : null;
  const campaignColumns = generateGridColumns(campaignGridColumns, "campaign_name");
  const campaignRows = generateGridRows(campaignsData, campaignRowMapping);

  const handleDownloadAndReload = () => {
    saveAs(zipFile.blob, zipFile.name);
    setZipFile(null);
    setIsUploaded(false);
    setFile(null);
    setSelectedCampaign(null);
    setInfo(null);
    setSelectedRowId(null)
  };

  const handleClearAndRetry = () => {
    setError(null);
    setUploadError("");
    setInfo(null);
    setIsUploaded(false);
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
    setZipFile(null);
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <output aria-label="Please wait...">
          <Spinner animation="border" />
        </output>
      </div>
    );
  }
  return userRole === "customer" ? (
    <>
      {isUploading && (
        <div className="processing-overlay" aria-busy="true">
          <div className="processing-box">
            <output aria-label="Processing contacts...">
              <Spinner animation="border" />
            </output>
            <p className="processing-text">
              <span>Processing contacts...</span>{" "}
              <span>Please wait.</span>
            </p>
          </div>
        </div>
      )}
      <div className="upload-contacts-container">
        <PageHelmet
          title="Upload Contacts"
          description="Upload and process your contacts with TraQR."
          url={`${process.env.REACT_APP_REDIRECT_URI}/contacts`}
        />
        <h1 className="main-heading">Upload Contacts</h1>
        {campaignsData.length === 0 ? (
          <div className="no-campaigns">
            <Alert variant="danger">
              No existing campaigns were found for this user.
            </Alert>
          </div>
        ) : (
          <>
            <h4 className="subheading-1">
              Select a campaign to upload contacts
            </h4>
            <p className="upload-para">
              <span className='subheading'>Mandatory CSV columns: </span>
              ID, envelope_name, greeting_name,
              ma-addr_line1, ma-city, ma-state, ma-zip. {' '}
              <a href="/sample.csv" download className='csv-link'>Download Sample CSV</a>
            </p>
            <div className="create-main-container ">
              <div
                className={`create-grid-form ${selectedCampaign ? "grid-to-left" : "grid-centered"
                  } `}
              >
                <div className="create-grid-container">
                  <CustomDataGrid
                    rows={campaignRows}
                    columns={campaignColumns}
                    totalRows={campaignsData.length}
                    onRowClick={handleRowClick}
                    selectedRowId={selectedRowId}
                  />
                </div>
                {selectedCampaign && (

                  <div className="create-form-container upload-contacts-right">
                    <EntityDetails inputs={campaignFormData} width={"100px"} />
                  </div>
                )}
              </div>

              {uploadError && (
                <Alert
                  data-testid="error-alert"
                  variant="danger"
                  className="upload-error-alert"
                >
                  {uploadError}
                </Alert>
              )}

              <div className='contacts-message-container'>
                {error && (
                  <>
                    <Alert data-testid="error-alert" variant="danger" style={{ marginBottom: "0" }}>
                      {error}
                    </Alert>
                    <Button
                      variant="danger"
                      className="upload-button"
                      style={{ marginTop: "10px", marginBottom: "5px" }}
                      onClick={handleClearAndRetry}
                    >
                      Clear message and retry
                    </Button>
                  </>
                )}

                {info && (
                  <Alert
                    data-testid="success-alert"
                    variant="success"
                    className="info-alert"
                  >
                    {info}
                  </Alert>
                )}
              </div>

              {selectedCampaign && (
                <div className="upload-contacts-center">
                  <div className="file-upload-container">
                    <p className="upload-heading">{`Upload CSV file for campaign '${selectedCampaign.campaign_name}'`}</p>
                    <div className="upload-area">
                      <label htmlFor="fileInput" className="upload-label">
                        <div className="upload-box">
                          <FaCloudUploadAlt size={20} color="black" />
                          <span className="upload-browse-span">Browse</span>
                        </div>
                        <span className="upload-drag-span">
                          or drag and drop
                        </span>
                      </label>
                      <input
                        type="file"
                        id="fileInput"
                        accept=".csv"
                        onChange={handleFileChange}
                        className="file-input"
                        ref={fileInputRef}
                      />
                    </div>


                    {file && (
                      <div className="file-list">
                        <div className="file-item">
                          <span>{file.name}</span>
                          <FaCheckCircle className="icon-success" />
                          <FaTrashAlt
                            className="icon-delete"
                            onClick={handleRemoveFile}
                          />
                        </div>
                      </div>
                    )}

                    <Button
                      variant="dark"
                      className="upload-button"
                      onClick={handleUpload}
                      disabled={!file || isUploaded}
                    >
                      Upload
                    </Button>
                  </div>


                  {zipFile && testMode && (
                    <Button
                      variant="success"
                      className="upload-button download-button"
                      onClick={handleDownloadAndReload}
                    >
                      <FaDownload size={20} style={{ marginRight: "8px" }} />
                      Download CSV and QR Codes
                    </Button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  ) : (
    <div className="user-container">
      <Alert variant="danger">You do not have access to this page.</Alert>
    </div>
  );
};

export default UploadContacts;
